// src/components/CatalogPage/CatalogPageSection.tsx
"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "motion/react";
import {
  X,
  FileText,
  Lock,
  MessageSquare,
  Search,
  SlidersHorizontal,
  Loader2,
} from "lucide-react";
import { catalogsData, type CatalogItem } from "@/data/catalogs";

const ITEMS_PER_PAGE = 6;

const ALL_CATEGORIES = [
  "All",
  ...Array.from(new Set(catalogsData.map((c) => c.category))),
];

export function CatalogPageSection() {
  const [activePdfUrl, setActivePdfUrl] = useState<string | null>(null);
  const [activeCatalogTitle, setActiveCatalogTitle] = useState<string>("");
  const [openedCatalogIds, setOpenedCatalogIds] = useState<string[]>([]);
  const [showContactPopup, setShowContactPopup] = useState<boolean>(false);

  // Filter & search state
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [showFilters, setShowFilters] = useState(false);

  // Infinite scroll state
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [isLoading, setIsLoading] = useState(false);
  const loaderRef = useRef<HTMLDivElement>(null);

  // Persist viewed history
  useEffect(() => {
    const savedHistory = localStorage.getItem("viewed_catalogs_history");
    if (savedHistory) {
      try {
        setOpenedCatalogIds(JSON.parse(savedHistory));
      } catch (e) {
        console.error("Error reading catalog logs", e);
      }
    }
  }, []);

  // Filtered catalog list
  const filteredCatalogs = catalogsData.filter((catalog) => {
    const matchesCategory =
      activeCategory === "All" || catalog.category === activeCategory;
    const matchesSearch =
      catalog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      catalog.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      catalog.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const visibleCatalogs = filteredCatalogs.slice(0, visibleCount);
  const hasMore = visibleCount < filteredCatalogs.length;

  // Reset pagination when filter/search changes
  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
  }, [activeCategory, searchQuery]);

  // Intersection observer for infinite scroll
  const loadMore = useCallback(() => {
    if (isLoading || !hasMore) return;
    setIsLoading(true);
    // Simulate async fetch delay
    setTimeout(() => {
      setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
      setIsLoading(false);
    }, 800);
  }, [isLoading, hasMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );
    const current = loaderRef.current;
    if (current) observer.observe(current);
    return () => {
      if (current) observer.unobserve(current);
    };
  }, [loadMore]);

  const handleCatalogClick = (catalog: CatalogItem) => {
    if (openedCatalogIds.includes(catalog.id)) {
      setShowContactPopup(true);
      return;
    }
    const updatedHistory = [...openedCatalogIds, catalog.id];
    setOpenedCatalogIds(updatedHistory);
    localStorage.setItem(
      "viewed_catalogs_history",
      JSON.stringify(updatedHistory)
    );
    setActiveCatalogTitle(catalog.title);
    setActivePdfUrl(`${catalog.pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`);
  };

  const handleContactAction = () => {
    window.location.href =
      "mailto:info@yourdomain.com?subject=Catalog Access Request";
    setShowContactPopup(false);
  };

  return (
    <section className="w-full bg-white px-6 py-16 md:px-12 lg:px-20 select-none">
      <div className="mx-auto max-w-[1340px]">

        {/* ── Page Header ── */}
        <div className="mb-12 flex flex-col items-start max-w-[700px]">
          <span className="text-[12px] font-bold tracking-[0.3em] text-[#a49d91] uppercase mb-3">
            Complete Library
          </span>
          <h1 className="text-[clamp(2rem,4vw,3.5rem)] font-black leading-[1.1] tracking-tight text-neutral-900 uppercase">
            All Design <br />Catalogues
          </h1>
          <p className="mt-4 text-[14.5px] leading-relaxed text-neutral-500 font-light max-w-[500px]">
            Browse our complete library of handcrafted design collections.
            Each catalog opens once — contact us for full access.
          </p>
        </div>

        {/* ── Search & Filter Bar ── */}
        <div className="mb-10 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          {/* Search input */}
          <div className="relative flex-1 max-w-[420px]">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <input
              type="text"
              placeholder="Search catalogs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3.5 rounded-full border border-neutral-200 bg-neutral-50 text-sm text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-400 transition-colors"
            />
          </div>

          {/* Filter toggle (mobile) */}
          <button
            onClick={() => setShowFilters((p) => !p)}
            className="sm:hidden flex items-center gap-2 px-5 py-3.5 rounded-full border border-neutral-200 text-xs font-bold uppercase tracking-wider text-neutral-700 bg-neutral-50"
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filters
          </button>

          {/* Category pills — desktop always visible */}
          <div className="hidden sm:flex flex-wrap gap-2">
            {ALL_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all cursor-pointer ${
                  activeCategory === cat
                    ? "bg-neutral-900 text-white"
                    : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile filter pills */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden sm:hidden mb-6"
            >
              <div className="flex flex-wrap gap-2 pb-2">
                {ALL_CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => {
                      setActiveCategory(cat);
                      setShowFilters(false);
                    }}
                    className={`px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all cursor-pointer ${
                      activeCategory === cat
                        ? "bg-neutral-900 text-white"
                        : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Results count */}
        <div className="mb-6 flex items-center justify-between">
          <p className="text-[13px] text-neutral-400 font-light">
            Showing{" "}
            <span className="font-semibold text-neutral-700">
              {visibleCatalogs.length}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-neutral-700">
              {filteredCatalogs.length}
            </span>{" "}
            catalogs
          </p>
        </div>

        {/* ── Catalog Grid ── */}
        {filteredCatalogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="h-16 w-16 bg-neutral-100 rounded-full flex items-center justify-center mb-5">
              <FileText className="h-7 w-7 text-neutral-400" />
            </div>
            <h3 className="text-lg font-black uppercase tracking-tight text-neutral-800">
              No catalogs found
            </h3>
            <p className="mt-2 text-sm text-neutral-400 font-light">
              Try adjusting your search or filter criteria.
            </p>
            <button
              onClick={() => {
                setSearchQuery("");
                setActiveCategory("All");
              }}
              className="mt-6 px-6 py-3 rounded-full bg-neutral-900 text-white text-xs font-bold uppercase tracking-wider hover:bg-neutral-800 transition-colors cursor-pointer"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {visibleCatalogs.map((catalog, index) => (
                <motion.div
                  key={catalog.id}
                  layout
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4, delay: (index % ITEMS_PER_PAGE) * 0.07 }}
                  whileHover={{ y: -5 }}
                  onClick={() => handleCatalogClick(catalog)}
                  className="group cursor-pointer rounded-[20px] overflow-hidden border border-neutral-100 bg-neutral-50 flex flex-col shadow-sm hover:shadow-lg transition-shadow duration-300"
                >
                  {/* Image area */}
                  <div className="relative h-[240px] overflow-hidden bg-neutral-100">
                    <Image
                      src={catalog.imageSrc}
                      alt={catalog.imageAlt}
                      fill
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                      className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />

                    {/* Viewed badge */}
                    {openedCatalogIds.includes(catalog.id) && (
                      <div className="absolute top-3 right-3 z-10">
                        <span className="flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-700 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">
                          <Lock className="h-2.5 w-2.5" /> Viewed
                        </span>
                      </div>
                    )}

                    {/* Category badge */}
                    <div className="absolute top-3 left-3 z-10">
                      <span className="bg-white/90 backdrop-blur-sm text-neutral-700 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">
                        {catalog.category}
                      </span>
                    </div>

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/25 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <span className="bg-white text-neutral-900 px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-sm flex items-center gap-2">
                        <FileText className="h-3.5 w-3.5" />
                        {openedCatalogIds.includes(catalog.id)
                          ? "Restricted"
                          : "View Catalog"}
                      </span>
                    </div>
                  </div>

                  {/* Card body */}
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h3 className="text-[13px] font-black uppercase tracking-tight text-neutral-900 leading-tight">
                        {catalog.title}
                      </h3>
                      <span className="shrink-0 text-[11px] text-neutral-400 font-light mt-0.5">
                        {catalog.year}
                      </span>
                    </div>
                    <p className="text-[12.5px] text-neutral-500 font-light leading-relaxed flex-1">
                      {catalog.description}
                    </p>
                    <div className="mt-4 pt-4 border-t border-neutral-100 flex items-center justify-between">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">
                        PDF Catalog
                      </span>
                      <span
                        className={`text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full ${
                          openedCatalogIds.includes(catalog.id)
                            ? "bg-amber-50 text-amber-600"
                            : "bg-neutral-100 text-neutral-600"
                        }`}
                      >
                        {openedCatalogIds.includes(catalog.id)
                          ? "Viewed"
                          : "Available"}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* ── Infinite scroll sentinel ── */}
        <div ref={loaderRef} className="mt-12 flex justify-center">
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center gap-3"
            >
              <Loader2 className="h-7 w-7 text-neutral-400 animate-spin" />
              <span className="text-xs text-neutral-400 font-light uppercase tracking-widest">
                Loading more catalogs...
              </span>
            </motion.div>
          )}
          {!hasMore && filteredCatalogs.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center gap-2"
            >
              <div className="h-px w-24 bg-neutral-200" />
              <span className="text-xs text-neutral-400 font-light uppercase tracking-widest">
                All catalogs loaded
              </span>
            </motion.div>
          )}
        </div>
      </div>

      {/* ── PDF Viewer Modal ── */}
      <AnimatePresence>
        {activePdfUrl && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col bg-neutral-950/95 p-4 md:p-8 select-none"
            onContextMenu={(e) => e.preventDefault()}
          >
            <div className="flex justify-between items-center w-full max-w-7xl mx-auto mb-4 text-white">
              <div className="flex flex-col">
                <h4 className="text-xs font-bold uppercase tracking-widest text-neutral-400">
                  Secure Digital Preview
                </h4>
                <p className="text-sm font-semibold text-white mt-0.5">
                  {activeCatalogTitle}
                </p>
              </div>
              <button
                onClick={() => setActivePdfUrl(null)}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider cursor-pointer transition-colors"
              >
                Close View <X className="h-4 w-4" />
              </button>
            </div>
            <div className="w-full max-w-7xl mx-auto flex-1 bg-neutral-900 rounded-xl overflow-hidden shadow-2xl">
              <object
                data={activePdfUrl}
                type="application/pdf"
                className="w-full h-full"
              >
                <iframe
                  src={activePdfUrl}
                  className="w-full h-full border-0"
                  title="Protected Portfolio View"
                />
              </object>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Access Restricted Modal ── */}
      <AnimatePresence>
        {showContactPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/70 p-4 backdrop-blur-md"
          >
            <motion.div
              initial={{ scale: 0.95, y: 15 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 15 }}
              className="bg-white w-full max-w-md rounded-2xl p-8 shadow-2xl text-center flex flex-col items-center"
            >
              <div className="h-14 w-14 bg-amber-50 rounded-full flex items-center justify-center text-amber-600 mb-5">
                <Lock className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-black uppercase text-neutral-900 tracking-tight">
                Access Restricted
              </h3>
              <p className="mt-3 text-[14px] leading-relaxed text-neutral-500 font-light">
                This catalog has already been opened once. To access or download
                it again, please contact us directly.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 w-full">
                <button
                  type="button"
                  onClick={handleContactAction}
                  className="flex-1 rounded-md bg-neutral-900 py-3.5 text-xs font-bold uppercase tracking-widest text-white hover:bg-neutral-800 transition-colors cursor-pointer flex items-center justify-center gap-2"
                >
                  <MessageSquare className="h-3.5 w-3.5" /> Contact Now
                </button>
                <button
                  type="button"
                  onClick={() => setShowContactPopup(false)}
                  className="flex-1 rounded-md bg-neutral-100 py-3.5 text-xs font-bold uppercase tracking-widest text-neutral-700 hover:bg-neutral-200 transition-colors cursor-pointer"
                >
                  Close Window
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}